<template>
  <div class="form-row form-with-table">
    <!-- Admin Comments Section -->
    <form class="col-sm-12" v-if="isAdmin">
      <div class="d-flex justify-content-end">
        <div class="col-md-2">
          <drawer-component
            v-if="hasScreeningSchedule"
            title="Comments"
            size="70%"
            :open="true"
          >
            <general-comments
              :comments="basic_info.screening_status"
              :emailAddress="basic_info.email"
              :applicantId="candidateId"
              :statusable_type="basic_info.statusable_type"
              :statusable_id="basic_info.id"
            />
          </drawer-component>
          <div v-else class="screening-note">
            Add this profile to Screening to proceed.
          </div>
        </div>
      </div>
    </form>
    <form :class="{ 'loading-opacity': preloader, 'col-sm-12 p-4': isAdmin, 'col-md-12 mb-0': !isAdmin }" ref="form">
      <div class="d-flex flex-wrap">
        <!-- Basic Information Section -->
        <div class="form-group mb-3 col-md-12">
          <h3 class="text-center mb-0">Basic Information Details</h3>
        </div>
        <div class="form-group mb-3 col-md-12 align-items-center">
          <div v-if="basic_info.employeeImagePreview || basic_info.employee_picture_path" class="mt-3">
            <img
              :src="getFileUrl(basic_info.employeeImagePreview || basic_info.employee_picture_path)"
              alt="Employee Picture"
              class="img-fluid"
              style="max-width: 100%; height: 150px; border: 1px solid #ccc; padding: 5px"
            />
          </div>
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2"
            >Position Applied For <b class="text-danger">*</b></label
          >
          <app-input
            class="col-sm-12"
            type="text"
            :required="true"
            @keyup="
              (event) => textOnChange(event, 'position_applied', validationType.Alphabet)
            "
            v-model="basic_info.position_applied"
            placeholder="Position Applied For"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Title <b class="text-danger">*</b></label>
          <app-input
            class="col-sm-12"
            type="select"
            :required="true"
            :list="titleList"
            v-model="basic_info.title"
            placeholder="Select title"
          />
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Forename <b class="text-danger">*</b></label>
          <app-input
            class="col-sm-12"
            type="text"
            :required="true"
            @keyup="(event) => textOnChange(event, 'forename', validationType.Alphabet)"
            v-model="basic_info.forename"
            placeholder="Forename"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Middle Name </label>
          <app-input
            class="col-sm-12"
            type="text"
            @keyup="
              (event) => textOnChange(event, 'middle_name', validationType.Alphabet)
            "
            v-model="basic_info.middle_name"
            placeholder="Middle Name"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Surname <b class="text-danger">*</b></label>
          <app-input
            class="col-sm-12"
            type="text"
            :required="true"
            @keyup="(event) => textOnChange(event, 'surname', validationType.Alphabet)"
            v-model="basic_info.surname"
            placeholder="Surname"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">N.I. Number</label>
          <app-input
            class="col-sm-12"
            type="text"
            :max-length="9"
            v-model="basic_info.n_i_number"
            @keyup="
              (event) => textOnChange(event, 'n_i_number', validationType.AlphaNumeric)
            "
            placeholder="NI Number"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center no-print">
          <label class="col-sm-12 mb-2">Applicant Picture <b class="text-danger">*</b></label>
          <app-input
            class="col-sm-12"
            type="file"
            v-model="basic_info.employee_picture"
            v-on:change="employeePictureChange"
          />
          
          <div v-if="basic_info.pending_profile_pic || basic_info.employee_picture_path || basic_info.employeeImagePreview" class="col-sm-12 document-preview-container mt-3">
            <div class="document-preview-card" @click="previewFile(basic_info.employeeImagePreview || basic_info.employee_picture_path)" style="cursor: pointer;">
              <div class="file-thumbnail-box" v-if="basic_info.pending_profile_pic">
                <button type="button" class="clear-file-btn" @click.stop="clearProfilePicture">
                  <app-icon name="x" style="width: 14px; height: 14px;" />
                </button>
                
                <img v-if="isImage(basic_info.employee_picture_name || basic_info.employee_picture_path)" 
                     :src="getFileUrl(basic_info.employeeImagePreview || basic_info.employee_picture_path)" 
                     alt="Profile Picture">
                
                <div v-else class="file-icon-large">
                  <app-icon :name="getFileIcon(basic_info.employee_picture_name || basic_info.employee_picture_path)" style="width: 48px; height: 48px;" />
                </div>
              </div>
              <div class="file-name-text" :title="basic_info.employee_picture_name">
                {{ basic_info.employee_picture_name || (basic_info.employee_picture_path || basic_info.employeeImagePreview ? getFileName(basic_info.employee_picture_path || basic_info.employeeImagePreview) : '') }}
              </div>
              <div class="upload-status-badge">
                <app-icon name="check-circle" style="width: 14px; height: 14px;" />
                {{ basic_info.pending_profile_pic ? 'Selected' : 'Uploaded' }}
                <app-icon name="check" style="width: 14px; height: 14px;" />
                <button v-if="!basic_info.pending_profile_pic" 
                        type="button" 
                        class="ml-2 text-danger p-0 border-0 bg-transparent"
                        @click.stop="clearProfilePicture">
                    <app-icon name="x" style="width: 14px; height: 14px;" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center" v-if="isAdmin">
          <label class="col-sm-12 mb-2">Country of birth: </label>
          <app-input
            class="col-sm-12"
            type="text"
            :max-date="today"
            v-model="basic_info.country_of_birth"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center" v-if="isAdmin">
          <label class="col-sm-12 mb-2">D.O.B: </label>
          <app-input
            class="col-sm-12"
            type="date"
            v-model="basic_info.date_of_birth"
            :max-date="today"
            placeholder=""
          />
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Nationality <b class="text-danger"> *</b></label>
          <app-input
            class="col-sm-12"
            type="select"
            :list="countryList"
            v-model="basic_info.nationality"
            placeholder="Nationality"
          />
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">EDBS Number (12 - Digits)</label>
          <app-input
            class="col-sm-12"
            type="text"
            @keyup="
              (event) => textOnChange(event, 'edbs_number', validationType.Numeric, 12)
            "
            v-model="basic_info.edbs_number"
            placeholder="EDBS Number"
          />
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Contact No. <b class="text-danger">*</b></label>
          <app-input
            class="col-sm-12"
            type="tel-input-custom"
            @keyup="
              (event) => textOnChange(event, 'contact_number', validationType.Numeric)
            "
            :required="true"
            v-model="basic_info.contact_number"
            placeholder="Contact No"
          />
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Alternative Contact No.</label>
          <app-input
            class="col-sm-12"
            type="tel-input-custom"
            @keyup="
              (event) =>
                textOnChange(event, 'alternative_contact_no', validationType.Numeric)
            "
            v-model="basic_info.alternative_contact_no"
            placeholder="Alternative Contact Number"
          />
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Email</label>
          <app-input
            class="col-sm-12"
            type="email"
            @keyup="(event) => textOnChange(event, 'email', validationType.Email)"
            v-model="basic_info.email"
            placeholder="Email"
            :read-only="true"
          />
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center" v-if="!basic_info.download_application">
          <label class="col-sm-12 mb-2">Additional Email</label>
          <app-input
              class="col-sm-12"
              type="email"
              @keyup="(event) => textOnChange(event, 'additional_email', validationType.Email)"
              v-model="basic_info.additional_email"
              placeholder="Email"
          />
        </div>
        <div class="form-group mb-3 col-md-6" v-if="!basic_info.download_application">
          <label class="mb-2">Email for Report</label>

          <div class="d-flex justify-content-around mt-3">
            <label>
              <input
                  type="radio"
                  value="primary"
                  v-model="basic_info.report_email"
              />
              Use Email
            </label>

            <label>
              <input
                  type="radio"
                  value="additional"
                  v-model="basic_info.report_email"
              />
              Use Additional Email
            </label>
          </div>
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Next of Kin <b class="text-danger">*</b></label>
          <app-input
            class="col-sm-12"
            type="text"
            :required="true"
            @keyup="
              (event) => textOnChange(event, 'next_of_kin', validationType.Alphabet)
            "
            v-model="basic_info.next_of_kin"
            placeholder="Next of kin"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Relationship <b class="text-danger">*</b></label>
          <app-input
            class="col-sm-12"
            type="select"
            :required="true"
            :list="relationShipTypeList"
            v-model="basic_info.relationship"
            placeholder="Relationship with kin"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2"
            >Next of Kin Contact No. <b class="text-danger">*</b></label
          >
          <app-input
            class="col-sm-12"
            type="tel-input-custom"
            :required="true"
            @keyup="
              (event) =>
                textOnChange(event, 'next_of_kin_contact_no', validationType.Numeric)
            "
            v-model="basic_info.next_of_kin_contact_no"
            placeholder="Next of kin contact number"
          />
        </div>

        <!-- Licenses Section -->
        <hr />
        <div class="form-group mb-3 col-md-12">
          <h3 class="text-center mb-0">Licenses (SIA/Others)</h3>
        </div>

        <!-- SIA LICENSES SECTION -->
        <div v-for="(license, index) in licenses" :key="license.uid" class="col-md-12 p-0 d-flex flex-wrap border-bottom mb-4 pb-3">
          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2"
              >License Type <b class="text-danger">*</b></label
            >
            <app-input
              class="col-sm-12"
              type="select"
              :required="true"
              :list="licenseOptions"
              v-model="license.type"
              placeholder="Select License"
            />
          </div>

          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2"
              >License Number <b class="text-danger">*</b></label
            >
            <app-input
              class="col-sm-12"
              :type="licenseTypeFieldType(license.type)"
              :required="true"
              v-model="license.number"
              placeholder="Enter License Number"
            />
          </div>

          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2">Issue Date (Optional)</label>
            <app-input
              type="date"
              class="col-sm-12"
              v-model="license.issueDate"
              :max-date="today"
              placeholder="Select Issue Date"
            />
          </div>

          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2"
              >License Expiry <b class="text-danger">*</b></label
            >
            <app-input
              class="col-sm-12"
              type="date"
              :required="true"
              v-model="license.expiry"
              :min-date="today"
              placeholder="Select Expiry Date"
            />
          </div>

          <!-- License Upload Field -->
          <div class="form-group mb-3 col-md-6 no-print">
            <label class="col-sm-12 mb-2">Upload License <b class="text-danger">*</b></label>
            <app-input
              class="col-sm-12"
              type="file"
              :id="'license_document_'+index"
              v-model="license.license_document"
              @change="(event) => licenseDocumentChange(event, index)"
            />
            
            <div v-if="license.pending_license_document || license.license_path || license.licensePreview" class="col-sm-12 document-preview-container mt-3">
              <div class="document-preview-card" @click="previewFile(license.licensePreview || license.license_path)" style="cursor: pointer;">
                <div class="file-thumbnail-box" v-if="license.pending_license_document">
                  <button type="button" class="clear-file-btn" @click.stop="clearLicenseDocument(index)">
                    <app-icon name="x" style="width: 14px; height: 14px;" />
                  </button>
                  
                  <img v-if="isImage(license.license_name || license.license_path)" 
                       :src="getFileUrl(license.licensePreview || license.license_path)" 
                       alt="License Preview">
                  
                  <div v-else class="file-icon-large">
                    <app-icon :name="getFileIcon(license.license_name || license.license_path)" style="width: 48px; height: 48px;" />
                  </div>
                </div>
                <div class="file-name-text" :title="license.license_name">
                  {{ license.license_name || (license.license_path || license.licensePreview ? getFileName(license.license_path || license.licensePreview) : '') }}
                </div>
                <div class="upload-status-badge">
                  <app-icon name="check-circle" style="width: 14px; height: 14px;" />
                  {{ license.pending_license_document ? 'Selected' : 'Uploaded' }}
                  <app-icon name="check" style="width: 14px; height: 14px;" />
                  <button v-if="!license.pending_license_document" 
                          type="button" 
                          class="ml-2 text-danger p-0 border-0 bg-transparent"
                          @click.stop="clearLicenseDocument(index)">
                      <app-icon name="x" style="width: 14px; height: 14px;" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Remove button for additional licenses only -->
          <div
            class="form-group mb-3 col-md-6 d-flex align-items-end no-print"
            v-if="licenses.length > 1"
          >
            <button class="btn btn-danger w-100" @click.prevent="removeLicense(index)">
              Remove License
            </button>
          </div>
        </div>

        <!-- Single Add License button shown once -->
        <div class="form-group mb-3 col-md-12 text-end no-print">
          <button class="btn btn-primary ml-3" @click.prevent="addLicense(true)">
            Add License
          </button>
        </div>

        <hr />
        <div class="form-group mb-3 col-md-12">
          <label class="col-sm-12 mb-2"> Do you have Driving License? </label>
          <app-input
            class="col-sm-12"
            :key="basic_info.is_driving_license"
            type="radio"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_driving_license"
          />
        </div>

        <template v-if="basic_info.is_driving_license === 'yes'">
          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2">Driving License No</label>
            <app-input
              class="col-sm-12 mr-sm-2"
              type="text"
              :max-length="16"
              v-model="basic_info.driver_license_number"
              @keyup="
                (event) =>
                  textOnChange(
                    event,
                    'driver_license_number',
                    validationType.AlphaNumeric
                  )
              "
              placeholder=""
            />
            <div class="error col-sm-12" v-if="!validation.driver_license_number">
              {{ $t("driving_license_error") }}
            </div>
          </div>
          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2">Driving License Type</label>
            <app-input
              class="col-sm-12"
              type="select"
              :list="drivingLicenseTypeList"
              v-model="basic_info.license_type"
              placeholder=""
            />
          </div>
          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2">Issue Date</label>
            <app-input
              class="col-sm-12"
              type="date"
              :max-date="today"
              v-model="basic_info.license_issue_date"
              placeholder=""
            />
          </div>
          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2">Expiry Date</label>
            <app-input
              class="col-sm-12"
              type="date"
              :min-date="today"
              v-model="basic_info.license_expiry_date"
              placeholder=""
            />
          </div>
          <div class="form-group mb-3 col-md-6 no-print">
            <label class="col-sm-12 mb-2">Upload Driving License <b class="text-danger">*</b></label>
            <app-input
              class="col-sm-12"
              type="file"
              v-model="basic_info.driving_license_document"
              v-on:change="drivingLicenseDocumentChange"
            />
            
            <div v-if="basic_info.pending_driving_license || basic_info.driving_license_path || basic_info.drivingLicensePreview" class="col-sm-12 document-preview-container mt-3">
              <div class="document-preview-card" @click="previewFile(basic_info.drivingLicensePreview || basic_info.driving_license_path)" style="cursor: pointer;">
                <div class="file-thumbnail-box" v-if="basic_info.pending_driving_license">
                  <button type="button" class="clear-file-btn" @click.stop="clearDrivingLicense">
                    <app-icon name="x" style="width: 14px; height: 14px;" />
                  </button>
                  
                  <img v-if="isImage(basic_info.driving_license_name || basic_info.driving_license_path)" 
                       :src="getFileUrl(basic_info.drivingLicensePreview || basic_info.driving_license_path)" 
                       alt="Driving License Preview">
                  
                  <div v-else class="file-icon-large">
                    <app-icon :name="getFileIcon(basic_info.driving_license_name || basic_info.driving_license_path)" style="width: 48px; height: 48px;" />
                  </div>
                </div>
                <div class="file-name-text" :title="basic_info.driving_license_name">
                  {{ basic_info.driving_license_name || (basic_info.driving_license_path || basic_info.drivingLicensePreview ? getFileName(basic_info.driving_license_path || basic_info.drivingLicensePreview) : '') }}
                </div>
                <div class="upload-status-badge">
                  <app-icon name="check-circle" style="width: 14px; height: 14px;" />
                  {{ basic_info.pending_driving_license ? 'Selected' : 'Uploaded' }}
                  <app-icon name="check" style="width: 14px; height: 14px;" />
                  <button v-if="!basic_info.pending_driving_license" 
                          type="button" 
                          class="ml-2 text-danger p-0 border-0 bg-transparent"
                          @click.stop="clearDrivingLicense">
                      <app-icon name="x" style="width: 14px; height: 14px;" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Right to Work Section -->
        <hr />
        <div class="form-group mb-3 col-md-12">
          <h3 class="text-center mb-0">Right to Work</h3>
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2"
            >Do you need visa to work or remain in the UK?</label
          >
          <app-input
            class="col-sm-12"
            type="radio"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_visa_require_uk"
          />
        </div>
        <div class="w-100"></div>

        <div
          v-if="basic_info.is_visa_require_uk === 'no'"
          class="form-group mb-3 col-md-6 align-items-center"
        >
          <label class="col-sm-12 mb-2"
            >Upload proof of right to work (Passport / Birth Certificate)
            <b class="text-danger">*</b></label
          >
          <app-input
            class="col-sm-12"
            type="file"
            v-model="basic_info.passport"
            v-on:change="passportPictureChange"
          />

          <div v-if="basic_info.pending_passport || basic_info.passport_path || basic_info.passportPreview" class="col-sm-12 document-preview-container mt-3">
            <div class="document-preview-card" @click="previewFile(basic_info.passportPreview || basic_info.passport_path)" style="cursor: pointer;">
              <div class="file-thumbnail-box" v-if="basic_info.pending_passport">
                <button type="button" class="clear-file-btn" @click.stop="clearPassportPicture">
                  <app-icon name="x" style="width: 14px; height: 14px;" />
                </button>
                
                <img v-if="isImage(basic_info.passport_name || basic_info.passport_path)" 
                     :src="getFileUrl(basic_info.passportPreview || basic_info.passport_path)" 
                     alt="Passport Preview">
                
                <div v-else class="file-icon-large">
                  <app-icon :name="getFileIcon(basic_info.passport_name || basic_info.passport_path)" style="width: 48px; height: 48px;" />
                </div>
              </div>
              <div class="file-name-text" :title="basic_info.passport_name">
                {{ basic_info.passport_name || (basic_info.passport_path || basic_info.passportPreview ? getFileName(basic_info.passport_path || basic_info.passportPreview) : '') }}
              </div>
              <div class="upload-status-badge">
                <app-icon name="check-circle" style="width: 14px; height: 14px;" />
                {{ basic_info.pending_passport ? 'Selected' : 'Uploaded' }}
                <app-icon name="check" style="width: 14px; height: 14px;" />
                <button v-if="!basic_info.pending_passport" 
                        type="button" 
                        class="ml-2 text-danger p-0 border-0 bg-transparent"
                        @click.stop="clearPassportPicture">
                    <app-icon name="x" style="width: 14px; height: 14px;" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- (Visa Details, Other Employment, Others Sections...) -->
        <!-- ✅ To keep this message size manageable, I can paste the remaining section (visa type, BRP, share code, etc.) in a follow-up if you want. -->
        <!-- CONTINUATION: Right to Work Section -->

        <template v-if="basic_info.is_visa_require_uk === 'yes'">
          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2">Visa Type <b class="text-danger">*</b></label>
            <app-input
              class="col-sm-12"
              type="select"
              :required="true"
              :list="visaTypeList"
              v-model="basic_info.visa_type"
              placeholder=""
            />
          </div>

          <template v-if="basic_info.visa_type === 'others'">
            <div class="form-group mb-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-2"
                >Specify Visa Type <b class="text-danger">*</b></label
              >
              <app-input
                class="col-sm-12"
                type="text"
                :required="true"
                v-model="basic_info.visa_type_others"
                placeholder="Input visa type"
              />
            </div>
          </template>

          <div class="form-group mb-3 col-md-6 align-items-center" v-if="basic_info.visa_type !== 'Leave To Remain'">
            <label class="col-sm-12 mb-2">Visa Expiry <b class="text-danger">*</b></label>
            <app-input
              class="col-sm-12"
              type="date"
              :required="true"
              :min-date="today"
              v-model="basic_info.visa_expiry"
              placeholder=""
            />
          </div>

          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2"
              >Date of Entry / Re-Entry <b class="text-danger">*</b></label
            >
            <app-input
              class="col-sm-12"
              type="date"
              :required="true"
              :max-date="today"
              v-model="basic_info.date_of_entry_re_entry"
              placeholder=""
            />
          </div>

          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2">{{
              $t("Biometric Residence Permit Number (BRP) Number") + ' If Available'
            }}</label>
            <app-input
              class="col-sm-12"
              type="text"
              @keyup="
                (event) =>
                  textOnChange(
                    event,
                    'biometric_residence_permit',
                    validationType.AlphaNumeric
                  )
              "
              :max-length="9"
              v-model="basic_info.biometric_residence_permit"
              placeholder=""
            />
          </div>

          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2"
              >Share Code <b class="text-danger">*</b>
              <span class="ml-3">
                <a
                  href="https://right-to-work.service.gov.uk/rtw-prove/id-question"
                  target="_blank"
                  >Generate Share Code</a
                >
              </span>
            </label>
            <app-input
              class="col-sm-12"
              type="text"
              :required="true"
              :max-length="9"
              @keyup="
                (event) => textOnChange(event, 'share_code', validationType.AlphaNumeric)
              "
              v-model="basic_info.share_code"
              placeholder=""
            />
          </div>

          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2"
              >Share Code Expiry Date <b class="text-danger">*</b></label
            >
            <app-input
              class="col-sm-12"
              type="date"
              :required="true"
              v-model="basic_info.share_code_start_date"
              placeholder=""
            />
          </div>
        </template>

        <!-- Other Employment Section -->
        <div class="w-100"></div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Are you currently employed?</label>
          <app-input
            class="col-sm-12"
            type="radio"
            :list="yesOrNoList"
            :key="basic_info.is_other_employment"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_other_employment"
          />
        </div>
        <div class="w-100"></div>

        <template v-if="basic_info.is_other_employment === 'yes'">
          <div class="form-group mb-3 col-md-6 align-items-center">
            <label class="col-sm-12 mb-2"
              >Would you continue other employment, if you were successful in obtaining
              employment with us? <b class="text-danger">*</b></label
            >
            <app-input
              class="col-sm-12"
              type="text"
              :required="true"
              @keyup="
                (event) =>
                  textOnChange(
                    event,
                    'continue_other_employment',
                    validationType.Alphabet
                  )
              "
              v-model="basic_info.continue_other_employment"
              placeholder=""
            />
          </div>
        </template>

        <!-- Others Section -->
        <hr />
        <div class="form-group mb-3 col-md-6 col-md-12">
          <h3 class="text-center mb-0">Others</h3>
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Any Criminal Record?</label>
          <app-input
            class="col-sm-12"
            type="radio"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_criminal_record"
          />

          <template v-if="basic_info.is_criminal_record === 'yes'">
            <div class="mt-2 col-12 p-0">
              <label class="col-sm-12 mb-2">Criminal Record <b class="text-danger">*</b></label>
              <app-input
                class="col-sm-12"
                type="text"
                :required="true"
                @keyup="(event) => textOnChange(event, 'criminal_record_details', validationType.Alphabet)"
                v-model="basic_info.criminal_record_details"
                placeholder=""
              />
            </div>
          </template>
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Any Bankruptcy (or) CCJ in last 6 years?</label>
          <app-input
            class="col-sm-12"
            type="radio"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_bankruptcy"
          />

          <template v-if="basic_info.is_bankruptcy === 'yes'">
            <div class="mt-2 col-12 p-0">
              <label class="col-sm-12 mb-2">Bankruptcy (or) CCJ Record <b class="text-danger">*</b></label>
              <app-input
                class="col-sm-12"
                type="text"
                :required="true"
                @keyup="(event) => textOnChange(event, 'bankruptcy_details', validationType.Alphabet)"
                v-model="basic_info.bankruptcy_details"
                placeholder=""
              />
            </div>
          </template>
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Any days/ hours/shifts you can’t work?</label>
          <app-input
            class="col-sm-12"
            type="radio"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_days_shift_cant_work"
          />

          <template v-if="basic_info.is_days_shift_cant_work === 'yes'">
            <div class="mt-2 col-12 p-0">
              <label class="col-sm-12 mb-2">Shift Unavailability <b class="text-danger">*</b></label>
              <app-input
                class="col-sm-12"
                type="text"
                :required="true"
                v-model="basic_info.days_shift_cant_work_details"
                placeholder=""
              />
            </div>
          </template>
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2"
            >Do you have any holidays commitment in the future?</label
          >
          <app-input
            class="col-sm-12"
            type="radio"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_holidays_commitment"
          />

          <template v-if="basic_info.is_holidays_commitment === 'yes'">
            <div class="mt-2 col-12 p-0">
              <label class="col-sm-12 mb-2">Details of holidays commitment in the future.</label>
              <app-input
                class="col-sm-12"
                type="text"
                v-model="basic_info.holidays_commitment_details"
                placeholder=""
              />
            </div>
          </template>
        </div>
        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-sm-2"
            >Do you have access to your own transport?</label
          >
          <app-input
            type="radio"
            class="col-sm-12 mb-2"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_own_vehicle"
          />
        </div>

        <div class="form-group mb-3 col-md-6 align-items-center">
          <label class="col-sm-12 mb-2">Hobbies?</label>
          <app-input
            class="col-sm-12"
            :key="basic_info.is_leisure"
            type="radio"
            :list="yesOrNoList"
            radio-checkbox-wrapper="radio-left-align-gap"
            v-model="basic_info.is_leisure"
          />

          <template v-if="basic_info.is_leisure === 'yes'">
            <div class="mt-2 col-12 p-0">
              <label class="col-sm-12 mb-2">Please note your leisure interest and hobbies. <b class="text-danger">*</b></label>
              <app-input
                class="col-sm-12"
                type="text"
                :required="true"
                v-model="basic_info.leisure_details"
                placeholder=""
              />
            </div>
          </template>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { FormMixin } from "@/core/mixins/form/FormMixin";
import {
  genderList,
  countryList,
  siaLicenseTypeList,
  visaTypeList,
  employmentDetailsTypeList,
  p46List,
  drivingLicenseTypeList,
} from "@Views/Screening/utility/constant";
import {
  validateAlphabet,
  validateAlphaNumericSixteenCharacter,
  validateNumeric,
} from "@Views/Screening/utility/common/candidate";
import { validateAlphaNumeric } from "@Views/Screening/utility/common/candidate";
import { validationType } from "@Views/Screening/utility/enums";
import {
  relationShipTypeList,
  titleList,
} from "@Views/Screening/utility/constant/candidate";
import { File as FileApi } from "@Views/Screening/utility/apiAgent";
import AppLibrary from "@/app/Helpers/AppLibrary";

export default {
  name: "BasicInformation",
  extends: AppLibrary,
  mixins: [FormMixin],
  props: {
    preloader: {
      type: Boolean,
      default: false,
    },
    basic_info: {
      type: Object,
      default: () => ({}),
    },
    yesOrNoList: {
      type: Array,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    restrictValidation: {
      type: Boolean,
      default: false,
    },
    validationDefault: {
      type: Object,
      default: () => ({}),
    },
    candidateId: {
      type: Number,
    },
    onSaveBtn: {
      type: Function,
    },
  },
  data() {
    return {
      forename_error: false,
      employeeImagePreview: null, // URL for the image preview
      genderList: genderList,
      titleList: titleList,
      countryList: countryList,
      siaLicenseTypeList: siaLicenseTypeList,
      licenseOptions: siaLicenseTypeList,
      visaTypeList: visaTypeList,
      employmentDetailsTypeList: employmentDetailsTypeList,
      p46List: p46List,
      drivingLicenseTypeList: drivingLicenseTypeList,
      relationShipTypeList: relationShipTypeList,
      validationType: validationType,
      validation: {
        forename: true,
        middle_name: true,
        surname: true,
        driver_license_number: true,
        next_of_kin: true,
        relationship: true,
      },
      licenses: [], // User added licenses
    };
  },
  created() {
    this.initializeLicenses();
    this.$hub.$emit("candidateDetailActiveIndex", 0);
  },
  computed: {
    hasScreeningSchedule() {
      const s = this.basic_info?.screeningSchedule;
      if (!s) return false;
      if (Array.isArray(s)) return s.length > 0;
      if (typeof s === "object") return Object.keys(s).length > 0;
      return !!s;
    },
    today() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months start at 0
      const dd = String(today.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
    availableLicenses() {
      if (this.licenses && Array.isArray(this.licenses)) {
        const selectedIds = this.licenses.map((l) => l.type);
        return this.licenseOptions.filter((opt) => !selectedIds.includes(opt.id));
      } else {
        return [
          {
            type: "",
            number: "",
            expiry: "",
            issueDate: "",
            license_document: null,
            pending_license_document: null,
            license_path: null,
            license_id: null,
            license_name: null,
            licensePreview: null,
          },
        ]; // Return an empty array or a default value if licenses is undefined
      }
    },
  },
  watch: {
    // Watch for changes in the licenses array to manage disabled options
    "basic_info.licenses": {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length > 0) {
          this.licenses = newVal.map(l => ({
            ...l,
            uid: l.uid || Math.random().toString(36).substring(2, 11)
          }));
        } else {
          // initialize with a blank license row
          this.licenses = [this.createBlankLicense()];
        }
      },
    },
    licenses: {
      deep: true,
      handler(val) {
        this.$emit("update:licenses", val);
      },
    },
    "basic_info.visa_type": {
      immediate: true,
      handler(val) {
        if (val && val.trim() !== "" && val !== "Please Select") {
          this.ensureVisaTypeInList(val);
        }
      },
    },
  },
  methods: {
    createBlankLicense() {
      return {
        uid: Math.random().toString(36).substring(2, 11),
        type: "",
        number: "",
        expiry: "",
        issueDate: "",
        license_document: null,
        pending_license_document: null,
        license_path: null,
        license_id: null,
        license_name: null,
        licensePreview: null,
      };
    },
    initializeLicenses() {
      if (
        Array.isArray(this.basic_info.licenses) &&
        this.basic_info.licenses.length > 0
      ) {
        this.licenses = this.basic_info.licenses.map(l => ({
          ...l,
          uid: l.uid || Math.random().toString(36).substring(2, 11)
        }));
      } else {
        this.licenses = [this.createBlankLicense()];
      }
    },
    ensureVisaTypeInList(val) {
      const normalized = val.trim().toLowerCase();

      const exists = this.visaTypeList.some(
        (item) => item.id.trim().toLowerCase() === normalized
      );

      if (!exists) {
        this.visaTypeList.push({
          id: val,
          value: val,
        });
      }
    },
    licenseTypeFieldType(type) {
      if (type === "drivingLicense" || type === "nonFrontLine" || type === "others") {
        return "text";
      } else {
        return "number";
      }
    },
    addLicense(userAddition = false) {
      if (this.licenses.length === 0 || userAddition) {
        this.licenses.push(this.createBlankLicense());
      }
    },
    async removeLicense(index) {
      const license = this.licenses[index];
      if (license && license.license_id) {
        try {
          await FileApi.deleteDocument(license.license_id);
        } catch (e) {}
      }
      this.licenses.splice(index, 1);

      if (this.licenses.length === 0) {
        this.licenses.push(this.createBlankLicense());
      }

      // If onSaveBtn is provided, call it to persist the deletion immediately
      if (this.onSaveBtn && typeof this.onSaveBtn === 'function') {
        this.onSaveBtn();
      }
    },
    updateLicenseOptions(selectedType, isDisabled) {
      const license = this.licenseOptions.find((opt) => opt.id === selectedType);
      if (license) {
        license.disabled = isDisabled;
      }
    },
    validateAlphaNumericSixteenCharacter,
    validateAlphaNumeric,
    validateAlphabet,
    validateNumeric,

    employeePictureChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Create a URL for the image preview only if it's an image
        if (file.type.startsWith("image/")) {
          this.$set(
            this.basic_info,
            "employeeImagePreview",
            URL.createObjectURL(file)
          );
        } else {
          this.$set(this.basic_info, "employeeImagePreview", null);
        }
        this.$set(this.basic_info, "employee_picture_name", file.name);
        this.$set(this.basic_info, "pending_profile_pic", file);
      }
    },
    passportPictureChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Create a URL for the image preview only if it's an image
        if (file.type.startsWith("image/")) {
          this.$set(
            this.basic_info,
            "passportPreview",
            URL.createObjectURL(file)
          );
        } else {
          this.$set(this.basic_info, "passportPreview", null);
        }
        this.$set(this.basic_info, "passport_name", file.name);
        this.$set(this.basic_info, "pending_passport", file);
      } else if (this.basic_info.passport) {
        this.$set(
          this.basic_info,
          "passportPreview",
          this.basic_info.passport
        );
      }
    },
    clearProfilePicture() {
      // If there is a pending picture, just clear it (Deselect)
      if (this.basic_info.pending_profile_pic) {
        this.$set(this.basic_info, "employee_picture", null);
        this.$set(this.basic_info, "employeeImagePreview", null);
        this.$set(this.basic_info, "employee_picture_name", null);
        this.$set(this.basic_info, "pending_profile_pic", null);
        return;
      }

      // Only delete from DB if NO pending picture and ID exists (Admin only)
      if (this.basic_info.employee_picture_id) {
        if (confirm("Are you sure you want to delete this file?")) {
          FileApi.deleteDocument(this.basic_info.employee_picture_id)
            .then(() => {
              this.$toastr.s("File deleted successfully");
              this.$set(this.basic_info, "employee_picture", null);
              this.$set(this.basic_info, "employeeImagePreview", null);
              this.$set(this.basic_info, "employee_picture_name", null);
              this.$set(this.basic_info, "employee_picture_path", null);
              this.$set(this.basic_info, "pending_profile_pic", null);
              this.$set(this.basic_info, "employee_picture_id", null);
            })
            .catch(() => {
              this.$toastr.e("Failed to delete file");
            });
        }
      } else {
        // Fallback clear (should rarely be reached if pending logic catches it)
        this.$set(this.basic_info, "employee_picture", null);
        this.$set(this.basic_info, "employeeImagePreview", null);
        this.$set(this.basic_info, "employee_picture_name", null);
        this.$set(this.basic_info, "employee_picture_path", null);
        this.$set(this.basic_info, "pending_profile_pic", null);
      }
    },
    clearPassportPicture() {
      // If there is a pending passport, just clear it (Deselect)
      if (this.basic_info.pending_passport) {
        this.$set(this.basic_info, "passport", null);
        this.$set(this.basic_info, "passportPreview", null);
        this.$set(this.basic_info, "passport_name", null);
        this.$set(this.basic_info, "pending_passport", null);
        return;
      }

      // Only delete from DB if NO pending passport and ID exists (Admin only)
      if (this.basic_info.passport_id) {
        if (confirm("Are you sure you want to delete this file?")) {
          FileApi.deleteDocument(this.basic_info.passport_id)
            .then(() => {
              this.$toastr.s("File deleted successfully");
              this.$set(this.basic_info, "passport", null);
              this.$set(this.basic_info, "passportPreview", null);
              this.$set(this.basic_info, "passport_name", null);
              this.$set(this.basic_info, "passport_path", null);
              this.$set(this.basic_info, "pending_passport", null);
              this.$set(this.basic_info, "passport_id", null);
            })
            .catch(() => {
              this.$toastr.e("Failed to delete file");
            });
        }
      } else {
        // Fallback clear
        this.$set(this.basic_info, "passport", null);
        this.$set(this.basic_info, "passportPreview", null);
        this.$set(this.basic_info, "passport_name", null);
        this.$set(this.basic_info, "passport_path", null);
        this.$set(this.basic_info, "pending_passport", null);
      }
    },
    drivingLicenseDocumentChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Create a URL for the image preview only if it's an image
        if (file.type.startsWith("image/")) {
          this.$set(
            this.basic_info,
            "drivingLicensePreview",
            URL.createObjectURL(file)
          );
        } else {
          this.$set(this.basic_info, "drivingLicensePreview", null);
        }
        this.$set(this.basic_info, "driving_license_name", file.name);
        this.$set(this.basic_info, "pending_driving_license", file);
      } else if (this.basic_info.driving_license_document) {
        this.$set(
          this.basic_info,
          "drivingLicensePreview",
          this.basic_info.driving_license_document
        );
      }
    },
    clearDrivingLicense() {
      // If there is a pending license, just clear it (Deselect)
      if (this.basic_info.pending_driving_license) {
        this.$set(this.basic_info, "driving_license_document", null);
        this.$set(this.basic_info, "drivingLicensePreview", null);
        this.$set(this.basic_info, "driving_license_name", null);
        this.$set(this.basic_info, "pending_driving_license", null);
        return;
      }

      // Only delete from DB if NO pending license and ID exists (Admin only)
      if (this.basic_info.driving_license_id) {
        if (confirm("Are you sure you want to delete this file?")) {
          FileApi.deleteDocument(this.basic_info.driving_license_id)
            .then(() => {
              this.$toastr.s("File deleted successfully");
              this.$set(this.basic_info, "driving_license_document", null);
              this.$set(this.basic_info, "drivingLicensePreview", null);
              this.$set(this.basic_info, "driving_license_name", null);
              this.$set(this.basic_info, "driving_license_path", null);
              this.$set(this.basic_info, "pending_driving_license", null);
              this.$set(this.basic_info, "driving_license_id", null);
            })
            .catch(() => {
              this.$toastr.e("Failed to delete file");
            });
        }
      } else {
        // Fallback clear
        this.$set(this.basic_info, "driving_license_document", null);
        this.$set(this.basic_info, "drivingLicensePreview", null);
        this.$set(this.basic_info, "driving_license_name", null);
        this.$set(this.basic_info, "driving_license_path", null);
        this.$set(this.basic_info, "pending_driving_license", null);
      }
    },

    async licenseDocumentChange(event, index) {
      const file = event.target.files[0];
      const existingId = this.licenses[index]?.license_id;
      if (existingId) {
        try {
          await FileApi.deleteDocument(existingId);
        } catch (e) {}
      }
      if (file) {
        let formData = new FormData();
        formData.append('email', this.basic_info.email);
        formData.append('type', 'siaLicense');
        formData.append('files', file);
        const response = await FileApi.uploadDocument(formData);
        const uploadedFile = (response.data && response.data.data && response.data.data.length > 0) ? response.data.data[0] : null;
        if (uploadedFile && uploadedFile.file_path) {
          this.$set(this.licenses[index], 'license_path', uploadedFile.file_path);
          this.$set(this.licenses[index], 'license_id', uploadedFile.id);
          this.$set(this.licenses[index], 'license_name', uploadedFile.file_name);
        }
        this.$set(this.licenses[index], 'pending_license_document', file);
        this.$set(this.licenses[index], 'licensePreview', null);
        this.$set(this.licenses[index], 'license_document', null);
        // Create a URL for the image preview only if it's an image
        if (file.type.startsWith("image/")) {
          this.$set(
            this.licenses[index],
            "licensePreview",
              uploadedFile.file_path
          );
        } else {
          this.$set(this.licenses[index], "licensePreview", null);
        }
      } else if (this.licenses[index].license_document) {
        this.$set(
          this.licenses[index],
          "licensePreview",
          this.licenses[index].license_document
        );
      }
      if(this.onSaveBtn){
        this.onSaveBtn()
      }
    },
    clearLicenseDocument(index) {
      const license = this.licenses[index];
      // If there is a pending license, just clear it (Deselect)
      // if (license.pending_license_document) {
      //   this.$set(license, "license_document", null);
      //   this.$set(license, "licensePreview", null);
      //   this.$set(license, "license_name", null);
      //   this.$set(license, "pending_license_document", null);
      //   return;
      // }

      // Only delete from DB if NO pending license and ID exists (Admin only)
      if (license.license_id) {
        if (confirm("Are you sure you want to delete this file?")) {
          FileApi.deleteDocument(license.license_id)
            .then(() => {
              this.$toastr.s("File deleted successfully");
              this.$set(license, "license_document", null);
              this.$set(license, "licensePreview", null);
              this.$set(license, "license_name", null);
              this.$set(license, "license_path", null);
              this.$set(license, "pending_license_document", null);
              this.$set(license, "license_id", null);
              if(this.onSaveBtn){
                this.onSaveBtn()
              }

            })
            .catch(() => {
              this.$toastr.e("Failed to delete file");
            });
        }
      } else {
        // Fallback clear
        this.$set(license, "license_document", null);
        this.$set(license, "licensePreview", null);
        this.$set(license, "license_name", null);
        this.$set(license, "license_path", null);
        this.$set(license, "pending_license_document", null);
      }
    },
    getFileUrl(path) {
      if (!path) return '';
      if (path.toString().includes('blob:') || path.toString().includes('http')) {
        return path;
      }
      return this.getAppUrl(path) + '?t=' + new Date().getTime();
    },
    getFileIcon(fileName) {
      if (!fileName) return "file";
      const extension = fileName.split(".").pop().toLowerCase();
      if (extension === "pdf") return "file-text";
      return "file";
    },
    isImage(fileName) {
      if (!fileName) return false;
      const extension = fileName.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "svg"].includes(extension);
    },
    uploadBtnHandler(_file, _type, _issueDate = null, _expiryDate = null) {
      if (!this.basic_info.email) {
        this.$toastr.e("Email is required to upload documents.");
        return;
      }
      let data = { email: this.basic_info.email, type: _type, files: _file };
      if (_issueDate) {
        data.issue_date = _issueDate;
      }
      if (_expiryDate) {
        data.expiry_date = _expiryDate;
      }
      let formData = new FormData();
      for (const [key, value] of Object.entries(data)) {
        formData.append(key, value);
      }
      FileApi.uploadDocument(formData)
        .then((response) => {
          this.$toastr.s(response.data.message);
        })
        .catch(({ error }) => {
          //trigger after error
        })
        .finally(() => {
          if (this.tableId) {
            this.$hub.$emit("reload-" + this.tableId);
          }
        });
    },
    getFileName(path) {
      if (!path) return '';
      // If it's a blob/URL, try to get from name prop first (already handled by ||)
      // Otherwise split by / or \
      const parts = path.split(/[/\\]/);
      return parts[parts.length - 1];
    },
    previewFile(path) {
      if (!path) return;
      const url = this.getFileUrl(path);
      window.open(url, '_blank');
    },
    textOnChange(event, model, validationType, length = null) {
      const text = event?.target.value;
      if (text) {
        if (validationType === this.validationType.AlphaNumeric16Characters) {
          this.validation[model] = this.validateAlphaNumericSixteenCharacter(text);
        } else if (validationType === this.validationType.AlphaNumeric) {
          this.validation[model] = this.validateAlphaNumeric(text);
        } else if (validationType === this.validationType.Alphabet) {
          this.validation[model] = this.validateAlphabet(text);
        } else if (validationType === this.validationType.Numeric) {
          const validationData = this.validateNumeric(text, length);
          this.validation[model] = validationData.isValid;
          this.basic_info[model] = validationData.input;
        } else if (validationType === this.validationType.Email) {
          this.validation[model] = this.validateEmail(text);
        }
        if (!this.validation[model]) {
          // this.basic_info[field] = text.slice(0, -1);
          this.$set(this.basic_info, model, text.slice(0, -1));
          // event.preventDefault();
        }
        this.$set(this.validation, model, this.validation[model]);
      }
    },
  },
};
</script>

<style scoped>
hr {
  width: 100% !important;
}
.error {
  color: red;
  font-weight: bold;
}
.form-with-table .h-100 {
  height: auto !important;
}

::v-deep .radio-left-align-gap {
  display: flex !important;
  justify-content: flex-start !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}
::v-deep .radio-left-align-gap .customized-radio {
  margin-right: 20px !important;
  margin-bottom: 0 !important;
}

.document-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 10px;
  justify-content: flex-start;
  padding-left: 15px;
  padding-right: 15px;
}

.document-preview-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  animation: fadeIn 0.4s ease;
}

.file-thumbnail-box {
  width: 140px;
  height: 100px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.file-thumbnail-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.file-thumbnail-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 5px;
}

.file-icon-large {
  color: #6c757d;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-name-text {
  font-weight: 600;
  margin-top: 5px;
  font-size: 12px;
  color: #3498db;
  max-width: 100%;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;
}

.document-preview-card:hover .file-name-text {
  color: #2980b9;
  text-decoration: underline;
}

.upload-status-badge {
  color: #27ae60;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-weight: 600;
}

.clear-file-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  color: #e74c3c;
  z-index: 2;
  transition: all 0.2s;
}

.clear-file-btn:hover {
  background: #e74c3c;
  color: #fff;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
